import React, { useState } from 'react';
import { DriverFormData } from '../../types/driver';

interface UploadedFileUrls {
  profilePhoto: string | null;
  passportPhoto: string | null;
  insuranceCertificate: string | null;
  roadworthinessCertificate: string | null;
}

interface BackgroundStepProps {
  data: DriverFormData;
  updateData: (data: Partial<DriverFormData>) => void;
  onFileUpload: (files: File[], type: keyof UploadedFileUrls) => Promise<void>;
  uploadedUrls: UploadedFileUrls;
}

const BackgroundStep: React.FC<BackgroundStepProps> = ({
  data,
  updateData,
  onFileUpload,
  uploadedUrls,
}) => {
  const [uploading, setUploading] = useState<Record<string, boolean>>({});

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    updateData({ [name]: checked });
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: keyof UploadedFileUrls
  ) => {
    const { files } = e.target;
    if (files && files[0]) {
      setUploading((prev) => ({ ...prev, [type]: true }));
      try {
        await onFileUpload([files[0]], type);
      } catch {
        alert(`Failed to upload ${type}`);
      } finally {
        setUploading((prev) => ({ ...prev, [type]: false }));
      }
    }
  };

  const UploadField = ({
    label,
    type,
    required = false,
    hint,
  }: {
    label: string;
    type: keyof UploadedFileUrls;
    required?: boolean;
    hint?: string;
  }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required ? '*' : <span className="text-gray-400 font-normal">(Optional)</span>}
      </label>
      <input
        type="file"
        onChange={(e) => handleFileChange(e, type)}
        accept="image/*,.pdf"
        disabled={uploading[type]}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
        required={required}
      />
      <div className="mt-1.5 flex items-center gap-2">
        {uploading[type] && (
          <span className="text-blue-600 text-xs">Uploading...</span>
        )}
        {uploadedUrls[type] && !uploading[type] && (
          <span className="text-green-600 text-xs">✓ Uploaded successfully</span>
        )}
      </div>
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Background Checks & Documents
        </h2>

        {/* Background check declarations */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Background Check Declarations
          </h3>
          <div className="space-y-4">
            <div className="flex items-start">
              <input
                id="criminalRecordCheck"
                name="criminalRecordCheck"
                type="checkbox"
                checked={data.criminalRecordCheck}
                onChange={handleCheckboxChange}
                className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded mt-0.5"
                required
              />
              <div className="ml-3 text-sm">
                <label htmlFor="criminalRecordCheck" className="font-medium text-gray-700">
                  Criminal Record Check *
                </label>
                <p className="text-gray-500">
                  I declare that I have no criminal record or pending criminal charges.
                  I understand that providing false information may result in immediate termination.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <input
                id="drivingRecordCheck"
                name="drivingRecordCheck"
                type="checkbox"
                checked={data.drivingRecordCheck}
                onChange={handleCheckboxChange}
                className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded mt-0.5"
                required
              />
              <div className="ml-3 text-sm">
                <label htmlFor="drivingRecordCheck" className="font-medium text-gray-700">
                  Driving Record Check *
                </label>
                <p className="text-gray-500">
                  I declare that my driving record is clean with no major violations,
                  accidents, or license suspensions in the past 3 years.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Required photo uploads */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Required Photos</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <UploadField
              label="Profile Photo"
              type="profilePhoto"
              required
              hint="Clear, professional headshot. Visible to passengers."
            />
            <UploadField
              label="Passport Photograph"
              type="passportPhoto"
              required
              hint="White background passport-sized photo."
            />
          </div>

          {uploadedUrls.profilePhoto && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Profile Photo Preview:</p>
              <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-gray-300">
                <img
                  src={`${process.env.REACT_APP_API_URL}${uploadedUrls.profilePhoto}`}
                  alt="Profile preview"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}
        </div>

        {/* Optional document uploads */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">
            Additional Documents
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            These documents are optional but will strengthen your application.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <UploadField
              label="Vehicle Insurance Certificate"
              type="insuranceCertificate"
              hint="Upload any valid vehicle insurance document you have."
            />
            <UploadField
              label="Roadworthiness Certificate"
              type="roadworthinessCertificate"
              hint="FRSC roadworthiness certificate if available."
            />
          </div>
        </div>
      </div>

      {/* Notice */}
      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
        <div className="flex gap-3">
          <svg className="h-5 w-5 text-yellow-400 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div>
            <h3 className="text-sm font-medium text-yellow-800">Important Notice</h3>
            <p className="mt-1 text-sm text-yellow-700">
              By submitting this application, you authorize Xryde to verify the information
              provided. False declarations may result in permanent removal from the platform.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackgroundStep;